// React
import React, { useState, useEffect } from "react";
// redux hooks
import { useFieldArray, Controller } from "react-hook-form";
// BASE components
import BASESvgIcon from '../../../../../../../@BASE/core/BASESvgIcon';
import BASEUtils from "../../../../../../../@BASE/utils/BASEUtils";
// material-ui components
import { styled } from "@mui/material/styles";
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from "@mui/system/Box";
import Button from "@mui/material/Button"
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import IconButton from "@mui/material/IconButton";
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from "@mui/material/TextField";
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Typography from "@mui/material/Typography";
// custom components
import NumberedHeading from "../../../../../../shared-components/forms/NumberedHeading";
import PEBriefSegmentArray from './PEBriefSegmentArray';

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default function PEBriefArray({ control, register, setValue, getValues, errors, inputDir }) {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'content.core'
  })

  const [fileError, setFileError] = useState(null);
  
    const recreateSeconds = (second) => {
      if (second.includes(",")) {
        const split = second.split(",");
        return parseFloat(split[0] + "." + split[1]);
      }
  
      return second;
    };
  
    const separateTimeStamp = (time, timePeriod) => {
      if (typeof time !== "string" || !time.includes(":")) {
        console.warn("Invalid time format. Expected HH:MM:SS format.");
        return null;
      }
      const timeArray = time.split(":");
  
      switch (timePeriod) {
        case "hour":
          return parseInt(timeArray[0]);
        case "minute":
          return parseInt(timeArray[1]);
        case "second":
          return recreateSeconds(timeArray[2]);
        default:
          return null;
      }
    };
  
    const validateFile = (file) => {
      if (file.type !== "application/json" && !file.name.includes(".srt")) {
        setFileError("Invalid file type. Please upload a JSON or SRT file.");
        return false;
      }
      return true;
    };
  
    const readSRT = (data, index) => {
      const blocksArray = data.split("\r\n\r\n");
      // clean up the array by removing empty strings
      const filteredBlocks = blocksArray.filter((block) => block.trim() !== "");
  
      const ccArray = filteredBlocks.map((block, i) => {
        try {
          const blockArray = block.split("\r\n");
          // if the blockArray has less than 2 items, it's not a valid block
          if (blockArray.length < 2) {
            return null;
          }
  
          // const index = blockArray[0];
          const timestamps = blockArray[1].split(" --> ");
          // if the timestamps array has more than 2 items, it's not a valid block
          if (timestamps.length !== 2) {
            return null;
          }
          const id = BASEUtils.generateGUID();
          const review = {
              development: {
                status: "development",
                approved: false,
                approval_date: "",
              },
              production: {
                status: "not_started",
                approved: false,
                approval_date: "",
              },
            };
          const start_minute = separateTimeStamp(timestamps[0], "minute");
          const start_second = separateTimeStamp(timestamps[0], "second");
          const end_minute = separateTimeStamp(timestamps[1], "minute");
          const end_second = separateTimeStamp(timestamps[1], "second");
          const text_tl = blockArray.slice(2).join(" ");
          const description = "";
  
          return { id, review, start_minute, start_second, end_minute, end_second, text_tl, description };
        } catch (err) {
          return null;
        }
      });
      // one last cleanup to remove any null items
      const validCCArray = ccArray.filter((item) => item !== null);
  
      setValue(`content.core.${index}.segments`, validCCArray);
    };
  
    const readJSON = (result, index) => {
      const data = JSON.parse(result);
      const ccArray = data.map((ccItem) => {
        const id = BASEUtils.generateGUID();
        const review = {
              development: {
                status: "development",
                approved: false,
                approval_date: "",
              },
              production: {
                status: "not_started",
                approved: false,
                approval_date: "",
              },
            };
        const start_minute = separateTimeStamp(ccItem.start_time, "minute");
        const start_second = separateTimeStamp(ccItem.start_time, "second");
        const end_minute = separateTimeStamp(ccItem.end_time, "minute");
        const end_second = separateTimeStamp(ccItem.end_time, "second");
        const text_tl = ccItem.text;
        const description = "";

        return { id, review, start_minute, start_second, end_minute, end_second, text_tl, description };
      });
  
      setValue(`content.core.${index}.segments`, ccArray);
    };
  
    const getFile = (e, index) => {
      const file = e.target.files[0];
  
      if (!validateFile(file)) {
        return;
      }
      const reader = new FileReader();
      reader.readAsText(file);
      reader.onload = () => {
        try {
          if (file.name.includes(".srt")) {
            readSRT(reader.result, index);
          }
          if (file.type === "application/json") {
            readJSON(reader.result, index);
          }
        } catch (err) {
          setFileError("Invalid file. Please upload a real valid file.");
        }
      };
    };
  
    useEffect(() => {
      if (fileError) console.warn("File Error:", fileError);
    }, [fileError]);

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
        }}
        className="px-0"
      >
        {fields.map((item, index) => {
          return (
            <TimelineItem key={item.id}>
              <TimelineSeparator>
                <TimelineDot color="secondary" sx={{marginTop: '22px'}} />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className="py-0">
                <Accordion defaultExpanded className='bg-transparent shadow-none'>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon />} 
                    aria-controls={`${index}`}
                    id={`${index}`}
                    className="py-0 px-16 m-0 rounded bg-white shadow-sm"
                    sx={{border: 'solid 1px', borderColor: '#bdbdbd'}}
                  >
                    <Typography className="grow m-0 font-semibold" sx={{lineHeight: '28px'}}>
                      Scenario {index + 1}
                    </Typography>
                    {index !== 0 &&
                      <IconButton 
                        className="ml-16 mr-8"
                        aria-label="move up"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index - 1);
                        }}
                      >
                        <BASESvgIcon size={20}>heroicons-solid:arrow-circle-up</BASESvgIcon>
                      </IconButton>
                    }
                    {index !== fields.length - 1 &&
                      <IconButton 
                        className="mx-8"
                        aria-label="move down"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index + 1);
                        }}
                      >
                        <BASESvgIcon size={20}>heroicons-solid:arrow-circle-down</BASESvgIcon>
                      </IconButton>
                    }
                    <IconButton
                      className="ml-8 mr-16"
                      aria-label="delete"
                      color="error"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(index);
                      }}
                    >
                      <BASESvgIcon size={20}>heroicons-outline:trash</BASESvgIcon>
                    </IconButton>
                  </AccordionSummary>
                  <AccordionDetails className="p-0">
                    <Box className="p-0">

                      <NumberedHeading number="1" title="Difficulty" instructions='Select either "Single", "Base", "Intermediate", or "Advanced".' required />
                      <Controller
                        control={control}
                        name={`content.core.${index}.difficulty`}
                        render={({ field }) => (
                          <FormControl fullWidth className='mt-16' size="small" error={!!errors.content?.core?.[index]?.difficulty} required>
                            <InputLabel id="select-label">Difficulty</InputLabel>
                            <Select
                              {...field}
                              labelId="select-tag"
                              id="difficulty"
                              label="Difficulty"
                              classes={{ select: "flex items-center space-x-12" }}
                              defaultValue=""
                            >
                              <MenuItem value="" className="space-x-12" disabled>
                                Select a Tag
                              </MenuItem>
                              <MenuItem value="single" className="space-x-12">
                                Single
                              </MenuItem>
                              <MenuItem value="base" className="space-x-12">
                                Base
                              </MenuItem>
                              <MenuItem value="intermediate" className="space-x-12">
                                Intermediate
                              </MenuItem>
                              <MenuItem value="advanced" className="space-x-12">
                                Advanced
                              </MenuItem>
                            </Select>
                            {!!errors.content?.core?.[index]?.tag ? (
                              <FormHelperText>{errors.content?.core?.[index]?.difficulty?.message}</FormHelperText>
                            ) : null}
                          </FormControl>
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading number="2" title="Description" instructions="Enter a description for the video." required />
                      <Controller 
                        control={control}
                        name={`content.core.${index}.description`}
                        render={({ field }) => (
                          <>
                            <TextField 
                              {...field}
                              className="mt-16 mb-16"
                              label="Description"
                              placeholder="Segment Description"
                              error={!!errors.content?.core?.[index]?.description}
                              helperText={errors.content?.core?.[index]?.description?.message}
                              fullWidth
                              multiline
                              minRows={5}
                              maxRows={10}
                            />
                          </>
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading number="3" title="Transcript" instructions="Enter the full transcript for the video." required />
                      <Controller 
                        control={control}
                        name={`content.core.${index}.transcript`}
                        render={({ field }) => (
                          <>
                            <TextField 
                              {...field}
                              className="mt-16 mb-16"
                              label="Transcript"
                              placeholder="Transcript"
                              error={!!errors.content?.core?.[index]?.transcript}
                              helperText={errors.content?.core?.[index]?.transcript?.message}
                              fullWidth
                              multiline
                              minRows={5}
                              maxRows={10}
                              dir={inputDir}
                            />
                          </>
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading number="4" title="Video Segments" instructions="Using the controls below, create the necessary video segments. These segments will outline the video shot by shot and include all the necessary details." required />
                      <Button
                        component="label"
                        role={undefined}
                        variant="contained"
                        className="mt-16 mb-24"
                        color="secondary"
                        tabIndex={0}
                        startIcon={<BASESvgIcon>heroicons-solid:upload</BASESvgIcon>}
                      >
                        Upload
                        <VisuallyHiddenInput
                          type="file"
                          onChange={(e) => getFile(e, index)}
                          accept=".json, .srt"
                        />
                      </Button>
                      <Controller
                        control={control}
                        name={`content.core.${index}.segments`}
                        render={({field}) => <PEBriefSegmentArray nestIndex={index} inputDir={inputDir} {...{ control, errors }} />}
                      />

                      <Divider className="mt-16" />
                      
                    </Box>               
                  </AccordionDetails>
                  <AccordionActions>
                    {index !== 0 &&
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index - 1)}
                        startIcon={<BASESvgIcon size={20}>heroicons-solid:arrow-circle-up</BASESvgIcon>}
                        size="small"
                      >
                        Move Up
                      </Button>
                    }
                    {index !== fields.length - 1 &&
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index + 1)}
                        startIcon={<BASESvgIcon size={20}>heroicons-solid:arrow-circle-down</BASESvgIcon>}
                        size="small"
                      >
                        Move Down
                      </Button>
                    }
                    <Button
                      variant="contained"
                      onClick={() => remove(index)}
                      color="error"
                      startIcon={<BASESvgIcon size={20}>heroicons-outline:trash</BASESvgIcon>}
                      size="small"
                    >
                      Remove
                    </Button>
                  </AccordionActions>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          )                    
        })}
      </Timeline>
      <Box className="flex items-center mt-0 p-0">
        <Button
          className="group flex items-center mt-8 rounded cursor-pointer"
          color="secondary"
          variant="contained"
          onClick={() => append({ 
            id: BASEUtils.generateGUID(),
            review: {
              development: {
                status: "development",
                approved: false,
                approval_date: "",
              },
              production: {
                status: "not_started",
                approved: false,
                approval_date: "",
              },
            },
            difficulty: "",
            description: "",
            transcript: "",
            segments: [{ 
              id: BASEUtils.generateGUID(),
              review: {
                development: {
                  status: "development",
                  approved: false,
                  approval_date: "",
                },
                production: {
                  status: "not_started",
                  approved: false,
                  approval_date: "",
                },
              },
              description: "",
              text_tl: "",
              // text_en: "",
              start_minute: 0,
              start_second: 0,
              end_minute: 0,
              end_second: 0,
            }],
          })}
        >
          <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
          <span className="ml-8 font-medium text-secondary group-hover:underline">
            Add Scenario
          </span>
        </Button>
      </Box>
    </>
  )
}